// Pure replay logic, extracted from Replay.vue so it can be unit-tested without
// mounting a canvas. The component imports these; the math has no DOM deps.

import type { MapWindow, Sample, ActorTrack } from "./types";
import type { ArenaConfig } from "../config/arenas";

/** The map window active at scrub time `t`. Windows are gapless (see lib.rs
 *  maps()), so the loop covers everything between the first start and last end;
 *  before the first window we clamp to it, after the last we clamp to it. */
export function activeMapAt(maps: MapWindow[], t: number): MapWindow | null {
  if (!maps.length) return null;
  if (t <= maps[0].start_ms) return maps[0];
  for (const m of maps) {
    if (t >= m.start_ms && t < m.end_ms) return m;
  }
  return maps[maps.length - 1];
}

/** Largest entry <= t, or -Infinity if none. `key` maps an element to its time. */
function lastBefore<T>(arr: T[], t: number, key: (x: T) => number): number {
  let lo = 0, hi = arr.length;
  while (lo < hi) {
    const mid = (lo + hi) >> 1;
    if (key(arr[mid]) <= t) lo = mid + 1; else hi = mid;
  }
  return lo > 0 ? key(arr[lo - 1]) : -Infinity;
}

/** An actor is dead at `t` if their latest death <= t is more recent than their
 *  latest position sample <= t — i.e. no activity since dying. A battle-rez
 *  produces a later sample, flipping them back to alive automatically.
 *  Each actor carries its own sorted death times (from the wasm DTO). */
export function isDeadAt(actor: ActorTrack, t: number): boolean {
  const lastDeath = lastBefore(actor.deaths, t, (x) => x);
  if (lastDeath === -Infinity) return false;
  return lastDeath > lastBefore(actor.track, t, (s) => s.t);
}

/** Interpolated position at `t`. Clamps outside the track; never interpolates
 *  across a map change (would blend two coordinate systems) — holds at the
 *  earlier sample instead. */
export function sampleAt(track: Sample[], t: number): Sample | null {
  if (!track.length) return null;
  if (t <= track[0].t) return track[0];
  const last = track[track.length - 1];
  if (t >= last.t) return last;
  let lo = 0, hi = track.length - 1;
  while (lo + 1 < hi) {
    const mid = (lo + hi) >> 1;
    if (track[mid].t <= t) lo = mid; else hi = mid;
  }
  const a = track[lo], b = track[hi];
  if (a.map !== b.map) return a;
  const f = (t - a.t) / (b.t - a.t || 1);
  return {
    t,
    x: a.x + (b.x - a.x) * f,
    y: a.y + (b.y - a.y) * f,
    facing: a.facing,
    hp: a.hp + (b.hp - a.hp) * f,
    map: a.map,
  };
}

// ---- Map projection: world coords -> canvas pixels --------------------------
//
// Two pure stages:
//   1. world (posX,posY) -> image-pixel (px,py)   via the aspect-expanded bounds
//   2. image-pixel       -> canvas (cx,cy)         via the camera `view` matrix
//                                                   (identical to the editor)
// The SAME `imageToCanvas` matrix draws the art AND projects the unit dots, so
// they can never desync. The matrix comes from the authored `view` camera —
// exactly what the raidmapper editor showed.

export interface Projection {
  /** The image→canvas matrix to feed ctx.setTransform for drawing the art. */
  imageToCanvas: DOMMatrix;
  /** Project a world position onto the canvas via the same matrix. */
  project(posX: number, posY: number): [number, number];
}

/** Image-pixel → canvas DOMMatrix for a camera view. MUST match raidmapper's
 *  src/project.js viewMatrix exactly (editor and replay share this model):
 *    M = T(canvasCentre) · S(ppu) · R(θ) · T(-centre·imageSize)
 *  Fit by width (ppu = cw/iw · scale) so framing is independent of canvas
 *  aspect — the square editor and the wide replay show the same horizontal span
 *  for a view. ih only turns centerY (a fraction) into image px; fit is width-only. */
function viewMatrix(
  view: ArenaConfig["view"],
  iw: number,
  ih: number,
  cw: number,
  ch: number,
): DOMMatrix {
  const { centerX, centerY, rotation, scale } = view;
  const ppu = (cw / iw) * scale; // canvas px per image px
  return new DOMMatrix()
    .translateSelf(cw / 2, ch / 2)
    .scaleSelf(ppu, ppu)
    .rotateSelf(rotation)
    .translateSelf(-centerX * iw, -centerY * ih);
}

/**
 * Build the projection for a map. `bounds` is the full world rect the image
 * covers; the image is `iw`×`ih` pixels; the canvas is `cw`×`ch`. Bounds are
 * aspect-expanded to the image so world→image-pixel isn't distorted; then the
 * authored camera `view` maps image pixels onto the canvas.
 */
export function mapProjection(
  cfg: ArenaConfig,
  iw: number,
  ih: number,
  cw: number,
  ch: number,
): Projection {
  // --- aspect-expand the world bounds to match the image ---
  let { north, south, west, east } = cfg.bounds;
  const artAspect = iw / ih;
  const xSpan = north - south;
  const ySpan = west - east;
  if (ySpan / xSpan < artAspect) {
    const pad = (xSpan * artAspect - ySpan) / 2;
    west += pad;
    east -= pad;
  } else {
    const pad = (ySpan / artAspect - xSpan) / 2;
    north += pad;
    south -= pad;
  }
  const fullX = north - south;
  const fullY = west - east;

  const imageToCanvas = viewMatrix(cfg.view, iw, ih, cw, ch);

  return {
    imageToCanvas,
    project(posX: number, posY: number) {
      // world -> image pixel: west(maxPosY)->x=0, east->x=iw; north(maxPosX)->y=0.
      const px = ((west - posY) / fullY) * iw;
      const py = ((north - posX) / fullX) * ih;
      const p = imageToCanvas.transformPoint({ x: px, y: py });
      return [p.x, p.y] as [number, number];
    },
  };
}
